import { useEffect, useState } from 'react';
import api from '../../lib/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from '../ui/select';
import { toast } from 'sonner';
import { Wallet, Loader2, Info, ShieldAlert } from 'lucide-react';

const MAX_TOPUP = 100;

export default function AddCreditsTab() {
  const [orgs, setOrgs] = useState([]);
  const [accountId, setAccountId] = useState('');
  const [credits, setCredits] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get('/super-admin/credits/orgs')
      .then((r) => setOrgs(r.data?.orgs || []))
      .catch(() => toast.error('Could not load orgs'));
  }, []);

  const amount = Number(credits) || 0;
  const overCap = amount > MAX_TOPUP;
  const isOwner = (() => { try { return !!JSON.parse(localStorage.getItem('user'))?.is_owner; } catch { return false; } })();
  const blockedByCap = overCap && !isOwner;

  const submit = async () => {
    if (!accountId) return toast.error('Select an organization');
    if (amount <= 0) return toast.error('Enter a positive credit amount');
    setSaving(true);
    setResult(null);
    try {
      const { data } = await api.post('/super-admin/credits/admin-topup', {
        account_id: accountId, credits: amount, note,
      });
      setResult(data);
      toast.success(`Added ${amount} credits`);
      setCredits(''); setNote('');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Top-up failed');
    } finally { setSaving(false); }
  };

  return (
    <div className="max-w-xl" data-testid="add-credits-tab">
      <div className="mb-6">
        <h2 className="text-xl font-black tracking-tighter flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
          <Wallet className="h-5 w-5 text-primary" /> Add credits to an org
        </h2>
        <p className="text-sm text-slate-600 mt-2">
          Grant AI credits to any organization's wallet. Every top-up is logged (who, when, amount)
          and appears in the Credit Top-up Report.
        </p>
      </div>

      <div className="bg-white border border-slate-200 p-6 space-y-5">
        <div>
          <Label className="text-xs uppercase tracking-wide font-medium">Organization</Label>
          <Select value={accountId} onValueChange={setAccountId}>
            <SelectTrigger className="rounded-none mt-1" data-testid="add-credits-org-select">
              <SelectValue placeholder="Select an organization" />
            </SelectTrigger>
            <SelectContent>
              {orgs.map((o) => (
                <SelectItem key={o.account_id} value={o.account_id} data-testid={`add-credits-org-${o.account_id}`}>
                  {o.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div>
          <Label className="text-xs uppercase tracking-wide font-medium">Credits to add</Label>
          <Input
            type="number" min="1" step="1"
            value={credits}
            onChange={(e) => setCredits(e.target.value)}
            placeholder={`Max ${MAX_TOPUP} per top-up`}
            className="rounded-none mt-1 font-mono"
            data-testid="add-credits-amount"
          />
          <p className="text-[11px] text-slate-500 mt-1">1 credit ≈ $0.10 of AI/scraping usage.</p>
        </div>

        {overCap && (
          <div className="bg-amber-50 border border-amber-200 p-3 flex items-start gap-2 text-xs text-amber-800" data-testid="add-credits-owner-warning">
            <ShieldAlert className="h-4 w-4 mt-0.5 shrink-0" />
            {isOwner
              ? `Owner override: amounts over ${MAX_TOPUP} credits are allowed for you and flagged in the audit.`
              : `Amounts over ${MAX_TOPUP} credits require Owner permission — submit is disabled.`}
          </div>
        )}

        <div>
          <Label className="text-xs uppercase tracking-wide font-medium">Note (optional)</Label>
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason for this top-up…"
            className="rounded-none mt-1 min-h-[70px]"
            data-testid="add-credits-note"
          />
        </div>

        <div className="flex items-center justify-between pt-1">
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <Info className="h-3.5 w-3.5" /> Applied instantly to the org wallet.
          </div>
          <Button onClick={submit} disabled={saving || blockedByCap} className="rounded-none" data-testid="add-credits-submit">
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wallet className="h-4 w-4 mr-2" />}
            Add credits
          </Button>
        </div>

        {result?.ok && (
          <div className="bg-primary/5 border border-primary/20 p-3 text-sm text-slate-700" data-testid="add-credits-result">
            Added <b>{result.audit?.credits}</b> credits to <b>{result.audit?.org_name}</b>. New balance: <b>{result.balance_credits}</b>.
          </div>
        )}
      </div>
    </div>
  );
}
